// KPI ekran iskeleti (M16 T9): başlık + ContextBar + seçim kapısı. Müşteri seçilmemişse
// KpiEmptyState (kural 19); müşteri var set yoksa "set seçin / oluştur" notu; set varsa children.
// `requireSet=false` (Katalog) → müşteri/set olmadan da içerik render edilir.
// `fullHeight` (Veri Girişi): kök AppShell içerik alanının yüksekliğini alır
// (`calc(100vh - var(--layout-shell-header))` — FormFill kalıbı, değer shell'den gelir) ve flex
// sütun olur; başlık + ContextBar kendi yüksekliğini alır, children kalan alanı doldurur
// (çocuk `flex:1; min-height:0` ile kendi içinde kaydırır). Diğer ekranlar byte-eşit.
// `scrollBody` (Veri Yönetimi): `fullHeight` + gövde kendi dikey kaydırma kabıdır. AppShell'in
// `<main overflow-auto>`'u yükseklik sınırsız büyür ve belge kayar; o yüzden main içindeki
// `position: sticky; bottom` öğeler (SetChangesBar) main'in dibine yapışır, görünüme gelmez.
// Sınırlı yükseklikli gövde kaydırıcısı sticky'yi görünür alana bağlar (e2e 15 "2f").

import type { ReactNode } from "react";
import { KPI_COPY } from "../../lib/kpi-ui";
import { useKpiStore } from "../../stores/kpi-store";
import { ErrorState, LoadingState } from "../common/StateSurface";
import { KpiContextBar, type KpiContextBarProps } from "./KpiContextBar";
import { KpiEmptyState } from "./KpiEmptyState";
import { KpiPageHeader } from "./KpiPageHeader";

export interface KpiScreenProps extends KpiContextBarProps {
  title: string;
  subtitle?: string;
  headerRight?: ReactNode;
  requireSet?: boolean;
  /** içerik alanını dikeyde doldur (flex sütun) — grid ekranı */
  fullHeight?: boolean;
  /** `fullHeight` + gövde kendi içinde dikey kaydırır (sticky alt çubuk görünür kalsın) */
  scrollBody?: boolean;
  children: ReactNode;
  onCreateSetForClient?: (clientId: string) => void;
}

export function KpiScreen(props: KpiScreenProps): JSX.Element {
  const {
    title,
    subtitle,
    headerRight,
    requireSet = true,
    fullHeight: fullHeightProp = false,
    scrollBody = false,
    children,
    onCreateSetForClient,
    ...bar
  } = props;
  const fullHeight = fullHeightProp || scrollBody;
  const { clientId, setId, loading, error, clients, sets, grid } = useKpiStore();
  const client = clients.find((c) => c.id === clientId);
  const sub = subtitle ?? (client ? client.name : undefined);

  // güvenlik/UX taraması 2026-10-08 (U-H1): yükleme hatası + grid yokken sonsuz spinner yerine
  // ErrorState + yeniden dene (set varsa tabloyu, yoksa müşteriyi/listeyi yeniden yükler).
  const retry = (): void => {
    const s = useKpiStore.getState();
    if (s.setId) void s.refreshGrid();
    else if (s.clientId) void s.selectClient(s.clientId);
    else if (s.userId) void s.hydrate({ userId: s.userId });
    else void s.loadClients();
  };

  let body: ReactNode;
  if (loading && clients.length === 0) body = <LoadingState />;
  else if (error && !grid)
    body = <ErrorState message={error} onRetry={retry} retryLabel={KPI_COPY.grid.retry} />;
  else if (!clientId && requireSet)
    body = (
      <KpiEmptyState {...(onCreateSetForClient ? { onCreateSet: onCreateSetForClient } : {})} />
    );
  else if (!setId && requireSet)
    body = (
      <div
        className="rounded-xl p-6 text-sm"
        style={{
          border: "1px dashed var(--color-border)",
          color: "var(--color-text-2)",
          maxWidth: 760,
        }}
      >
        {sets.length > 0 ? (
          // P2-2: seti olan müşteride "seti yok" DENMEZ — otomatik seçim başarısız olduysa yönlendir
          KPI_COPY.selectSetPrompt
        ) : (
          <>
            {KPI_COPY.noSetForClient}.{" "}
            {bar.onCreateSet ? KPI_COPY.createSetFromBarHint : KPI_COPY.selectSetPrompt}
          </>
        )}
      </div>
    );
  else body = children;

  return (
    <div
      className="p-8"
      style={{
        fontFamily: "var(--font-sans)",
        maxWidth: 1600,
        ...(fullHeight
          ? {
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - var(--layout-shell-header, 0px))",
              minWidth: 0,
            }
          : {}),
      }}
    >
      <KpiPageHeader
        title={title}
        {...(sub ? { subtitle: sub } : {})}
        {...(headerRight ? { right: headerRight } : {})}
      />
      <KpiContextBar {...bar} />
      {scrollBody ? (
        <div data-testid="kpi-screen-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {body}
        </div>
      ) : fullHeight ? (
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {body}
        </div>
      ) : (
        body
      )}
    </div>
  );
}
